import { useState } from "react";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, Calendar as CalendarIcon, Clock, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import Layout from "@/components/Layout";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import TimeSlotSelector from "@/components/TimeSlotSelector";
import type { TimeSlot } from "@/lib/availabilityChecker";
import { HeroImage } from "@/components/HeroImage";

const appointmentSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20),
  serviceType: z.string().min(1, "Service type is required"),
  appointmentType: z.string().min(1, "Appointment type is required"),
  message: z.string().max(1000).optional(),
});

const ScheduleAppointment = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    serviceType: "",
    appointmentType: "",
    preferredDate: undefined as Date | undefined,
    message: ""
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.serviceType || !formData.appointmentType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (!selectedTimeSlot) {
      toast({
        title: "Missing Time Slot",
        description: "Please select a time slot for your appointment.",
        variant: "destructive"
      });
      return;
    }

    // Validate with zod
    try {
      appointmentSchema.parse(formData);
    } catch (err) {
      toast({
        title: "Invalid Information",
        description: "Please check your form data and try again.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the secure Edge Function to handle booking
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-appointment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.toLowerCase().trim(),
          phone: formData.phone.trim(),
          serviceType: formData.serviceType,
          appointmentType: formData.appointmentType,
          preferredDate: formData.preferredDate ? format(formData.preferredDate, 'yyyy-MM-dd') : null,
          startTimeISO: selectedTimeSlot.startTime.toISOString(),
          endTimeISO: selectedTimeSlot.endTime.toISOString(),
          requiresApproval: selectedTimeSlot.requiresApproval || false,
          message: formData.message?.trim() || null
        })
      });

      const result = await response.json();

      if (!result.success) {
        // Handle slot conflict specifically
        if (result.code === 'SLOT_CONFLICT') {
          toast({
            title: "Time Slot No Longer Available",
            description: "Someone just booked this time. Please select a different slot.",
            variant: "destructive"
          });
          // Clear the selected slot and trigger refresh
          setSelectedTimeSlot(null);
          return;
        }
        throw new Error(result.error || 'Failed to book appointment');
      }

      // Success - Show dialog instead of resetting form
      const message = selectedTimeSlot.requiresApproval
        ? `Your after-hours appointment request has been received! We'll review and confirm within 24 hours.`
        : `Appointment confirmed for ${format(selectedTimeSlot.startTime, 'MMMM d')} at ${format(selectedTimeSlot.startTime, 'h:mm a')}!`;

      setSuccessMessage(message);
      setShowSuccessDialog(true);
    } catch (error: any) {
      console.error('Error submitting appointment:', error);
      toast({
        title: "Submission Failed",
        description: error?.message || "There was an error submitting your appointment. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setShowSuccessDialog(false);
    // Reset form when dialog is closed
    setFormData({
      name: "",
      email: "",
      phone: "",
      serviceType: "",
      appointmentType: "",
      preferredDate: undefined,
      message: ""
    });
    setSelectedTimeSlot(null);
  };

  const handleNavigateToIntake = () => {
    setShowSuccessDialog(false);
    navigate("/new-client");
  };

  const appointmentSteps = [
    { number: "1", title: "Submit Request", description: "Fill out the form below" },
    { number: "2", title: "We'll Confirm", description: "Within 24 hours" },
    { number: "3", title: "Meet with Diane", description: "In-person or virtual" },
    { number: "4", title: "File Your Taxes", description: "Fast & secure" },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/hero-clover-waving.png"
            alt="Clover - The Tiny Tax Lady's Lucky Assistant"
            priority={true}
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">Schedule Your Appointment</h1>
            <p className="text-xl text-primary-foreground/90">
              Book a convenient time to meet with Diane and get your taxes done right
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
                <CheckCircle className="h-5 w-5" />
                <span>Same-day responses</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
                <CheckCircle className="h-5 w-5" />
                <span>Flexible scheduling</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
                <CheckCircle className="h-5 w-5" />
                <span>In-person or virtual</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 bg-gradient-to-r from-muted/30 via-background to-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {appointmentSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-accent text-white text-xl font-bold mb-3 shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="font-semibold mb-1">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <Card className="p-8 shadow-xl border-2">
                  <h2 className="text-2xl font-bold mb-6">Appointment Request Form</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Smith"
                        required
                      />
                    </div>

                    {/* Email & Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(913) 555-0123"
                          required
                        />
                      </div>
                    </div>

                    {/* Service Type */}
                    <div>
                      <Label htmlFor="serviceType">Service Type *</Label>
                      <Select
                        value={formData.serviceType}
                        onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Individual Tax Return">Individual Tax Return</SelectItem>
                          <SelectItem value="Business Tax Return">Sole Proprietor / Schedule C</SelectItem>
                          <SelectItem value="Tax Planning">Tax Planning / Consultation</SelectItem>
                          <SelectItem value="Other">Amended Return / Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Appointment Type */}
                    <div>
                      <Label htmlFor="appointmentType">Appointment Type *</Label>
                      <Select
                        value={formData.appointmentType}
                        onValueChange={(value) => setFormData({ ...formData, appointmentType: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select appointment type" />
                        </SelectTrigger>
                        <SelectContent className="bg-background border shadow-lg z-50">
                          <SelectItem value="in-person">In-Person (Lenexa, KS)</SelectItem>
                          <SelectItem value="virtual">Virtual Meeting</SelectItem>
                          <SelectItem value="drop-off">Drop off / Electronic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preferred Date */}
                    <div>
                      <Label>Preferred Date (Optional)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.preferredDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.preferredDate ? format(formData.preferredDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.preferredDate}
                            onSelect={(date) => setFormData({ ...formData, preferredDate: date })}
                            initialFocus
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time Slot Selector */}
                    <div>
                      <Label>Select Time Slot *</Label>
                      <TimeSlotSelector
                        selectedDate={formData.preferredDate}
                        selectedSlot={selectedTimeSlot}
                        onSlotSelect={setSelectedTimeSlot}
                        className="mt-2"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label htmlFor="message">Additional Notes (Optional)</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Any specific questions or details you'd like to share..."
                        rows={4}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full" 
                      disabled={isSubmitting || !selectedTimeSlot}
                    >
                      {isSubmitting ? "Submitting..." : selectedTimeSlot?.requiresApproval ? "Request Appointment" : "Confirm Appointment"}
                    </Button>
                  </form>
                </Card>
              </div>

              {/* Contact Info Sidebar */}
              <div className="space-y-6">
                <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-2">
                  <h3 className="font-bold text-xl mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Phone</p>
                        <a 
                          href="tel:913-406-7852" 
                          className="text-sm text-muted-foreground hover:text-secondary transition-colors"
                        >
                          913-406-7852
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Fax</p>
                        <p className="text-sm text-muted-foreground">
                          913-271-4416
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Email</p>
                        <a 
                          href="mailto:Diane@TinyTaxLady.com" 
                          className="text-sm text-muted-foreground hover:text-secondary transition-colors break-all"
                        >
                          Diane@TinyTaxLady.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Location</p>
                        <a 
                          href="https://www.google.com/maps/search/?api=1&query=9330+Gillette+St+Lenexa+KS+66215"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:text-secondary transition-colors"
                        >
                          9330 Gillette St.<br />Lenexa, KS 66215
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Hours</p>
                        <p className="text-sm text-muted-foreground">By appointment</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-accent/5 to-secondary/5 border-2 border-accent/20">
                  <h3 className="font-bold text-lg mb-3">What to Expect</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                      <span>We'll respond within 24 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                      <span>Flexible scheduling to fit your needs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                      <span>Upfront pricing with no surprises</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                      <span>Personal attention from Diane</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <DialogTitle className="text-center text-2xl">
              {selectedTimeSlot?.requiresApproval ? "Appointment Request Received!" : "Appointment Confirmed!"}
            </DialogTitle>
            <DialogDescription className="text-center space-y-4 pt-2">
              <p className="text-base">{successMessage}</p>
              
              {selectedTimeSlot && (
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {format(selectedTimeSlot.startTime, 'EEEE, MMMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {format(selectedTimeSlot.startTime, 'h:mm a')} - {format(selectedTimeSlot.endTime, 'h:mm a')}
                    </span>
                  </div>
                  <div className="text-xs text-center text-muted-foreground pt-1">
                    {formData.appointmentType === "in-person" ? "In-Person" : "Virtual"} • {formData.serviceType}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="font-semibold text-sm text-blue-900 mb-2">📋 What's Next?</p>
                <p className="text-sm text-blue-800">
                  Complete your client intake form to help us prepare for your appointment. You can also upload any necessary tax documents.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button variant="outline" onClick={handleCloseDialog}>
              Close
            </Button>
            <Button onClick={handleNavigateToIntake} className="gap-2">
              Complete Intake Form
              <ArrowRight className="h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ScheduleAppointment;
